import React from 'react';
import { Document, Image, Page, Text, View } from '@react-pdf/renderer';
import { formatCurrency, formatDecimal } from '../../../utils/format';
import { styles, formatDate } from './pdfShared';
import type { BusinessReportResponse, CustomerPurchaseDetailResponse, CustomerReportResponse, FinishedGoodsReportResponse, GreenBeansReportResponse, InventoryItemType, InventoryTrackingEvent, ReportMode, RoastedBeansReportResponse, RoastingReportResponse } from '../../../services/report.service';
import { InventoryTrackingPages } from './InventoryTrackingPages';
import { CustomerDetailPages } from './CustomerDetailPages';
import { BusinessDetailPages } from './BusinessDetailPages';
import { ReportPdfLogo } from './ReportPdfLogo';
import type { TFn } from '../export/context';

interface ReportPdfDocumentProps {
  mode: ReportMode;
  currencySymbol: string;
  startDate: string;
  endDate: string;
  roastingReport: RoastingReportResponse;
  greenBeansReport: GreenBeansReportResponse;
  roastedBeansReport: RoastedBeansReportResponse;
  finishedGoodsReport: FinishedGoodsReportResponse;
  businessReport: BusinessReportResponse;
  customerReport: CustomerReportResponse;
  t: TFn;
  inventoryTracking?: Map<string, InventoryTrackingEvent[]>;
  inventoryItemType?: InventoryItemType;
  customerDetails?: Map<string, CustomerPurchaseDetailResponse>;
  salesChartPng?: string;
}

const modeTitle: Record<ReportMode, string> = {
  roasting: 'Roasting Report',
  greenBeans: 'Green Beans Report',
  roastedBeans: 'Roasted Bulk Report',
  finishedGoods: 'Finished Good Report',
  business: 'Business Report',
  customer: 'Customer Report',
};

export const ReportPdfDocument: React.FC<ReportPdfDocumentProps> = ({
  mode,
  currencySymbol,
  startDate,
  endDate,
  roastingReport,
  greenBeansReport,
  roastedBeansReport,
  finishedGoodsReport,
  businessReport,
  customerReport,
  t,
  inventoryTracking,
  inventoryItemType,
  customerDetails,
  salesChartPng,
}) => {
  const inventoryRows =
    inventoryItemType === 'green_bean' ? greenBeansReport.rows
      : inventoryItemType === 'roasted_bulk' ? roastedBeansReport.rows
        : inventoryItemType === 'finished_good' ? finishedGoodsReport.rows
          : [];

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        <ReportPdfLogo />
        <Text style={styles.title}>{modeTitle[mode]}</Text>
        <Text style={styles.subtitle}>Period: {formatDate(startDate)} - {formatDate(endDate)}</Text>

        {mode === 'roasting' && (
          <>
            <View style={[styles.summaryGrid, { justifyContent: 'center' }]}>
              <View style={[styles.summaryCard, { width: '18%' }]}>
                <Text style={styles.summaryLabel}>Total Batches</Text>
                <Text style={styles.summaryValue}>{formatDecimal(roastingReport.summary.totalBatches, 0)}</Text>
              </View>
              <View style={[styles.summaryCard, { width: '18%' }]}>
                <Text style={styles.summaryLabel}>Total Input (kg)</Text>
                <Text style={styles.summaryValue}>{formatDecimal(roastingReport.summary.totalInputWeight, 2)}</Text>
              </View>
              <View style={[styles.summaryCard, { width: '18%' }]}>
                <Text style={styles.summaryLabel}>Total Output (kg)</Text>
                <Text style={styles.summaryValue}>{formatDecimal(roastingReport.summary.totalOutputWeight, 2)}</Text>
              </View>
              <View style={[styles.summaryCard, { width: '18%' }]}>
                <Text style={styles.summaryLabel}>Weight Loss (kg)</Text>
                <Text style={styles.summaryValue}>{formatDecimal(roastingReport.summary.totalWeightLoss, 2)}</Text>
              </View>
              <View style={[styles.summaryCard, { width: '18%' }]}>
                <Text style={styles.summaryLabel}>Avg Loss (%)</Text>
                <Text style={styles.summaryValue}>{formatDecimal(roastingReport.summary.averageLossPercent, 2)}%</Text>
              </View>
            </View>
            <View style={styles.table}>
              <View style={[styles.row, styles.headerRow]}>
                <Text style={[styles.cell, styles.headText, { flexGrow: 15 }]}>Date</Text>
                <Text style={[styles.cell, styles.headText, { flexGrow: 20 }]}>Batch</Text>
                <Text style={[styles.cell, styles.headText, { flexGrow: 27 }]}>Bean</Text>
                <Text style={[styles.cell, styles.headText, styles.right, { flexGrow: 12 }]}>Input</Text>
                <Text style={[styles.cell, styles.headText, styles.right, { flexGrow: 12 }]}>Output</Text>
                <Text style={[styles.cell, styles.headText, styles.right, { flexGrow: 14 }]}>Loss %</Text>
              </View>
              {roastingReport.rows.map(row => (
                <View key={row.id} style={styles.row}>
                  <Text style={[styles.cell, { flexGrow: 15 }]}>{formatDate(row.roastingDate)}</Text>
                  <Text style={[styles.cell, { flexGrow: 20 }]}>{row.batchNumber}</Text>
                  <Text style={[styles.cell, { flexGrow: 27 }]}>{row.greenBeanName}</Text>
                  <Text style={[styles.cell, styles.right, { flexGrow: 12 }]}>{formatDecimal(row.inputWeight, 2)}</Text>
                  <Text style={[styles.cell, styles.right, { flexGrow: 12 }]}>{formatDecimal(row.outputWeight, 2)}</Text>
                  <Text style={[styles.cell, styles.right, { flexGrow: 14 }]}>{formatDecimal(row.weightLossPercent, 2)}%</Text>
                </View>
              ))}
            </View>
          </>
        )}

        {mode === 'greenBeans' && (
          <>
            <View style={styles.summaryGrid}>
              <View style={styles.summaryCard}>
                <Text style={styles.summaryLabel}>Total Items</Text>
                <Text style={styles.summaryValue}>{formatDecimal(greenBeansReport.summary.totalItems, 0)}</Text>
              </View>
              <View style={styles.summaryCard}>
                <Text style={styles.summaryLabel}>Stock Value</Text>
                <Text style={styles.summaryValue}>
                  {formatCurrency(greenBeansReport.summary.totalStockValue, currencySymbol)}
                </Text>
              </View>
            </View>
            <View style={styles.table}>
              <View style={[styles.row, styles.headerRow]}>
                <Text style={[styles.cell, styles.headText, { flexGrow: 16 }]}>Code</Text>
                <Text style={[styles.cell, styles.headText, { flexGrow: 30 }]}>Item</Text>
                <Text style={[styles.cell, styles.headText, { flexGrow: 26 }]}>Supplier</Text>
                <Text style={[styles.cell, styles.headText, styles.right, { flexGrow: 14 }]}>Remaining</Text>
                <Text style={[styles.cell, styles.headText, styles.right, { flexGrow: 14 }]}>Avg Cost</Text>
              </View>
              {greenBeansReport.rows.map(row => (
                <View key={row.itemId} style={styles.row}>
                  <Text style={[styles.cell, { flexGrow: 16 }]}>{row.itemCode}</Text>
                  <Text style={[styles.cell, { flexGrow: 30 }]}>{row.itemName}</Text>
                  <Text style={[styles.cell, { flexGrow: 26 }]}>{row.supplierName}</Text>
                  <Text style={[styles.cell, styles.right, { flexGrow: 14 }]}>{formatDecimal(row.remainingQuantity, 2)}</Text>
                  <Text style={[styles.cell, styles.right, { flexGrow: 14 }]}>{formatCurrency(row.averageCost, currencySymbol)}</Text>
                </View>
              ))}
            </View>
          </>
        )}

        {mode === 'roastedBeans' && (
          <>
            <View style={styles.summaryGrid}>
              <View style={styles.summaryCard}>
                <Text style={styles.summaryLabel}>Total Items</Text>
                <Text style={styles.summaryValue}>{formatDecimal(roastedBeansReport.summary.totalItems, 0)}</Text>
              </View>
              <View style={styles.summaryCard}>
                <Text style={styles.summaryLabel}>Stock Value</Text>
                <Text style={styles.summaryValue}>
                  {formatCurrency(roastedBeansReport.summary.totalStockValue, currencySymbol)}
                </Text>
              </View>
            </View>
            <View style={styles.table}>
              <View style={[styles.row, styles.headerRow]}>
                <Text style={[styles.cell, styles.headText, { flexGrow: 16 }]}>Code</Text>
                <Text style={[styles.cell, styles.headText, { flexGrow: 30 }]}>Item</Text>
                <Text style={[styles.cell, styles.headText, { flexGrow: 26 }]}>Batch</Text>
                <Text style={[styles.cell, styles.headText, styles.right, { flexGrow: 14 }]}>Remaining</Text>
                <Text style={[styles.cell, styles.headText, styles.right, { flexGrow: 14 }]}>Avg Cost</Text>
              </View>
              {roastedBeansReport.rows.map(row => (
                <View key={row.itemId} style={styles.row}>
                  <Text style={[styles.cell, { flexGrow: 16 }]}>{row.itemCode}</Text>
                  <Text style={[styles.cell, { flexGrow: 30 }]}>{row.itemName}</Text>
                  <Text style={[styles.cell, { flexGrow: 26 }]}>{row.relatedBatch}</Text>
                  <Text style={[styles.cell, styles.right, { flexGrow: 14 }]}>{formatDecimal(row.remainingQuantity, 2)}</Text>
                  <Text style={[styles.cell, styles.right, { flexGrow: 14 }]}>{formatCurrency(row.averageCost, currencySymbol)}</Text>
                </View>
              ))}
            </View>
          </>
        )}

        {mode === 'finishedGoods' && (
          <>
            <View style={styles.summaryGrid}>
              <View style={styles.summaryCard}>
                <Text style={styles.summaryLabel}>Total Items</Text>
                <Text style={styles.summaryValue}>{formatDecimal(finishedGoodsReport.summary.totalItems, 0)}</Text>
              </View>
              <View style={styles.summaryCard}>
                <Text style={styles.summaryLabel}>Stock Value</Text>
                <Text style={styles.summaryValue}>
                  {formatCurrency(finishedGoodsReport.summary.totalStockValue, currencySymbol)}
                </Text>
              </View>
            </View>
            <View style={styles.table}>
              <View style={[styles.row, styles.headerRow]}>
                <Text style={[styles.cell, styles.headText, { flexGrow: 16 }]}>Code</Text>
                <Text style={[styles.cell, styles.headText, { flexGrow: 34 }]}>Item</Text>
                <Text style={[styles.cell, styles.headText, styles.right, { flexGrow: 16 }]}>Remaining (pcs)</Text>
                <Text style={[styles.cell, styles.headText, styles.right, { flexGrow: 16 }]}>Avg Cost</Text>
                <Text style={[styles.cell, styles.headText, { flexGrow: 18 }]}>Last Production</Text>
              </View>
              {finishedGoodsReport.rows.map(row => (
                <View key={row.itemId} style={styles.row}>
                  <Text style={[styles.cell, { flexGrow: 16 }]}>{row.itemCode}</Text>
                  <Text style={[styles.cell, { flexGrow: 34 }]}>{row.itemName}</Text>
                  <Text style={[styles.cell, styles.right, { flexGrow: 16 }]}>{formatDecimal(row.remainingQuantity, 0)}</Text>
                  <Text style={[styles.cell, styles.right, { flexGrow: 16 }]}>{formatCurrency(row.averageCost, currencySymbol)}</Text>
                  <Text style={[styles.cell, { flexGrow: 18 }]}>{formatDate(row.lastProductionDate)}</Text>
                </View>
              ))}
            </View>
          </>
        )}

        {mode === 'business' && (
          <>
            <View style={[styles.summaryGrid, { justifyContent: 'center' }]}>
              <View style={[styles.summaryCard, { width: '18%' }]}>
                <Text style={styles.summaryLabel}>Total Revenue</Text>
                <Text style={styles.summaryValue}>{formatCurrency(businessReport.summary.totalRevenue, currencySymbol)}</Text>
              </View>
              <View style={[styles.summaryCard, { width: '18%' }]}>
                <Text style={styles.summaryLabel}>Net Profit</Text>
                <Text style={styles.summaryValue}>{formatCurrency(businessReport.summary.netProfit, currencySymbol)}</Text>
              </View>
              <View style={[styles.summaryCard, { width: '18%' }]}>
                <Text style={styles.summaryLabel}>Total Orders</Text>
                <Text style={styles.summaryValue}>{formatDecimal(businessReport.summary.totalOrders, 0)}</Text>
              </View>
              <View style={[styles.summaryCard, { width: '18%' }]}>
                <Text style={styles.summaryLabel}>Active Customers</Text>
                <Text style={styles.summaryValue}>{formatDecimal(businessReport.summary.activeCustomers, 0)}</Text>
              </View>
              <View style={[styles.summaryCard, { width: '18%' }]}>
                <Text style={styles.summaryLabel}>Average Order Value</Text>
                <Text style={styles.summaryValue}>{formatCurrency(businessReport.summary.averageOrderValue, currencySymbol)}</Text>
              </View>
            </View>
            {salesChartPng && (
              <Image src={salesChartPng} style={{ width: '100%', height: 240, objectFit: 'contain', marginVertical: 8 }} />
            )}
          </>
        )}
        {mode === 'customer' && (
          <View style={styles.table}>
            <View style={[styles.row, styles.headerRow]}>
              <Text style={[styles.cell, styles.headText, { flexGrow: 40 }]}>Customer</Text>
              <Text style={[styles.cell, styles.headText, styles.right, { flexGrow: 18 }]}>Orders</Text>
              <Text style={[styles.cell, styles.headText, styles.right, { flexGrow: 18 }]}>Pcs</Text>
              <Text style={[styles.cell, styles.headText, styles.right, { flexGrow: 24 }]}>Revenue</Text>
            </View>
            {customerReport.customers.map(row => (
              <View key={row.customerId} style={styles.row}>
                <Text style={[styles.cell, { flexGrow: 40 }]}>{row.customerName}</Text>
                <Text style={[styles.cell, styles.right, { flexGrow: 18 }]}>{formatDecimal(row.orderCount, 0)}</Text>
                <Text style={[styles.cell, styles.right, { flexGrow: 18 }]}>{formatDecimal(row.totalPcs, 0)}</Text>
                <Text style={[styles.cell, styles.right, { flexGrow: 24 }]}>{formatCurrency(row.totalRevenue, currencySymbol)}</Text>
              </View>
            ))}
          </View>
        )}
      </Page>
        {mode === 'business' && (
          <BusinessDetailPages
            customerBreakdown={businessReport.customerBreakdown}
            history={businessReport.purchaseHistory}
            t={t}
            currencySymbol={currencySymbol}
          />
        )}
        {inventoryItemType && inventoryTracking && (
          <InventoryTrackingPages rows={inventoryRows} tracking={inventoryTracking} t={t} />
        )}
        {mode === 'customer' && customerDetails && (
          <CustomerDetailPages customers={customerReport.customers} details={customerDetails} t={t} currencySymbol={currencySymbol} />
        )}
    </Document>
  );
};
